/**
 * 차트 관련 유틸리티 함수들
 */

// 공통 색상 팔레트
export const COLOR_PALETTE = {
  primary: [
    'rgba(255, 99, 132, 0.8)',   // 빨강
    'rgba(54, 162, 235, 0.8)',   // 파랑
    'rgba(255, 206, 86, 0.8)',   // 노랑
    'rgba(75, 192, 192, 0.8)',   // 청록
    'rgba(153, 102, 255, 0.8)',  // 보라
    'rgba(255, 159, 64, 0.8)',   // 주황
    'rgba(199, 199, 199, 0.8)',  // 회색
    'rgba(83, 102, 255, 0.8)',   // 인디고
  ],
  
  // 품목별 고정 색상
  itemColors: {
    '나짱': 'rgba(255, 99, 132, 0.8)',    // 빨강
    '전주종': 'rgba(54, 162, 235, 0.8)',   // 파랑  
    '명장': 'rgba(255, 206, 86, 0.8)',     // 노랑
    '연진': 'rgba(75, 192, 192, 0.8)',     // 청록
    '전진': 'rgba(153, 102, 255, 0.8)',    // 보라
    '분류없음': 'rgba(201, 203, 207, 0.8)', // 회색
  },
  
  // 상태별 색상
  status: {
    success: 'rgba(34, 197, 94, 0.8)',    // 녹색
    warning: 'rgba(251, 191, 36, 0.8)',   // 노랑
    error: 'rgba(239, 68, 68, 0.8)',      // 빨강
    info: 'rgba(59, 130, 246, 0.8)',      // 파랑
    neutral: 'rgba(156, 163, 175, 0.8)',  // 회색
  }
};

// x축(월) 라벨 상수 배열
export const MONTH_LABELS = ["1월", "2월", "3월", "4월", "5월", "6월", "7월", "8월", "9월", "10월", "11월", "12월"];

// PRD 월별 콤보/라인차트에서 공통으로 사용하는 폰트 스타일
export const CHART_FONT = { size: 15, color: '#000000' };

/**
 * mm(1~12) 기준 월별 데이터 리스트를 라벨 순서(1월~12월)에 맞춰 숫자 배열로 변환
 * PRD502x~505x 차트들이 공통으로 사용하는 "월별 1개 필드 추출" 로직을 통합
 * @param data mm 필드를 가진 월별 데이터 리스트
 * @param field 추출할 숫자 필드명
 * @returns 1월~12월 순서로 정렬된 숫자 배열 (해당 월 데이터가 없으면 0)
 */
export const buildMonthlySeries = <T extends { mm: number }>(
  data: T[],
  field: keyof T,
): number[] => {
  return MONTH_LABELS.map((_, idx) => {
    const found = data.find((d) => d.mm === idx + 1);
    return Number(found?.[field] ?? 0);
  });
};

/**
 * RGBA 색상을 RGB로 변환
 * @param rgba RGBA 색상 문자열
 * @returns RGB 색상 문자열
 */
export const rgbaToRgb = (rgba: string): string => {
  return rgba.replace('rgba', 'rgb').replace(/, 0\.\d+\)/, ')');
};

/**
 * 호버 색상 생성 (투명도 증가)
 * @param rgba RGBA 색상 문자열
 * @returns 호버용 RGBA 색상 문자열
 */
export const getHoverColor = (rgba: string): string => {
  return rgba.replace('0.8)', '0.9)');
};

/**
 * 색상 배열 생성
 * @param count 필요한 색상 개수
 * @param palette 사용할 팔레트 (기본: primary)
 * @returns 색상 배열
 */
export const generateColors = (count: number, palette: string[] = COLOR_PALETTE.primary): string[] => {
  const colors: string[] = [];
  for (let i = 0; i < count; i++) {
    colors.push(palette[i % palette.length]);
  }
  return colors;
};

/**
 * 차트 데이터셋에 필요한 모든 색상 속성 생성
 * @param colors 기본 색상 배열
 * @returns 차트 데이터셋용 색상 객체
 */
export const generateChartColors = (colors: string[]) => ({
  backgroundColor: colors,
  borderColor: colors.map(rgbaToRgb),
  hoverBackgroundColor: colors.map(getHoverColor),
  borderWidth: 2,
});

/**
 * 빈 데이터 상태를 위한 기본 차트 데이터 생성
 * @param message 표시할 메시지
 * @returns 기본 차트 데이터
 */
export const getEmptyChartData = (message: string = '데이터 없음') => ({
  labels: [message],
  datasets: [{
    data: [1],
    ...generateChartColors([COLOR_PALETTE.status.neutral]),
  }]
});

/**
 * 퍼센티지 계산
 * @param value 값
 * @param total 전체
 * @param decimals 소수점 자릿수
 * @returns 퍼센티지 문자열
 */
export const calculatePercentage = (value: number, total: number, decimals: number = 2): string => {
  if (total === 0) return '0.00%';
  return `${((value / total) * 100).toFixed(decimals)}%`;
}; 